import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DatePicker from "components/common/DatePicker";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FileUpload from "components/common/FileUpload";
import FormButtons from "components/common/FormButtons";
import MultipleSelect from "components/common/MultipleSelect";
import SingleSelect from "components/common/SingleSelect";
import TimePeriodSelector from "components/common/TimePeriodSelector";
import { createWorkshopSchema } from "components/common/Validation";
import {
  genderOptions,
  locationTypeOptions,
} from "components/constants/DropdownOptions";
import { createWorkshopValues } from "components/constants/InitialFormValues";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Calendar } from "primereact/calendar";
import React, { useEffect, useState } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CircleX } from "lucide-react";
import { format, isValid, parse } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { showAlert } from "store/reducers/alertReducer";
import Switch from "components/common/Switch";
import Spinners from "components/common/Spinners";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCityList } from "store/actions/citySlice";
import { getAmenityList } from "store/actions/amenitySlice";
import {
  addWorkspace,
  getMeetingRoomsList,
  getWorkshopRequestDetails,
  getWorkspaceDetails,
  updateWorkshopRequest,
  updateWorkspace,
  getWorkspaceList,
  getWorkspaceBookingDetails,
  checkEntireBranch,
} from "store/actions/workspaceSlice";
import { getBranchListByCity } from "store/actions/branchSlice";
import {
  convertTo24HourFormat,
  formatDate,
  formatTime,
  getTimeDifference,
  toNumber,
} from "components/common/Utils";
import TimePicker from "components/common/TimePicker";
import {
  defaultEndTime,
  defaultStartTime,
} from "components/constants/Constants";

import { Editor } from "primereact/editor";
import { getBanksList } from "store/actions/globalSettingSlice";
import { resetRejectedRooms } from "store/reducers/workspaceReducer";
import { CalendarPrimaryIcon } from "components/common/SVGIcons";
const AddWorkshop = ({ mode }) => {
  const { t } = useTranslation();
  document.title =
    mode === "add"
      ? t("Add Workshop | Work Space")
      : mode === "edit"
      ? t("Edit Workshop | Work Space")
      : t("Edit Workshop Request | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const {
    isLoading,
    loading,
    workspaceDetails: workshopDetails,
    meetingRoomsList,
    rejectedRooms,
    workspaceList,
    workspaceBookingDetails,
    checkEntireBranchData,
  } = useSelector((state) => state.Workspace);

  const [formValues, setFormValues] = useState(createWorkshopValues);
  const [datetime12h, setDateTime12h] = useState(null);

  const { citiesList } = useSelector((state) => state.Cities);
  const { branchesByCity } = useSelector((state) => state.Branches);
  const { amenitiesList } = useSelector((state) => state.Amenities);
  const { banksList } = useSelector((state) => state.GlobalSettings);

  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const {brandInfo } = useSelector((state) => state.auth);
  const { primaryColor } = brandInfo?.theme?.themeColors  || {};

  useEffect(() => {
    dispatch(resetRejectedRooms());
    // GET CITY LIST
    const fetchCityList = () => {
      dispatch(getCityList());
    };
    // GET AMENITIES LIST
    const fetchAmenitiesList = () => {
      dispatch(getAmenityList());
    };
    // GET BANK LIST
    dispatch(getBanksList());

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkspaceDetails(payload));
    };

    const getRequestDetails = () => {
      const payload = {
        id,
      };
      dispatch(getWorkshopRequestDetails(payload));
    };

    fetchCityList();
    fetchAmenitiesList();
    mode === "edit" && getDetails();
    mode === "editWorkshopRequest" && getRequestDetails();
  }, [mode]);

  const fetchBranchesByCity = (cityId) => {
    const payload = {
      cityId,
    };
    dispatch(getBranchListByCity(payload));
  };

  const fetchMeetingRoomsList = (branchId, values) => {
    const { dateAndTimes, workShopDetails } = values || {};
    const { numberOfSeats } = workShopDetails || {};

    if (!dateAndTimes?.selectedDates?.length) {
      dispatch(
        showAlert({
          text: t(
            "Please select date and time slots for available meeting rooms"
          ),
          type: "error",
        })
      );
      return;
    }

    if (!numberOfSeats) {
      dispatch(
        showAlert({
          text: t("Please enter number of seats for available meeting rooms"),
          type: "error",
        })
      );

      return;
    }

    const selectedDateAndTimes = dateAndTimes?.selectedDates?.map((item) => {
      const dateObjStart = parse(item?.startTime?.start, "HH:mm", new Date());
      const start = format(dateObjStart, "hh:mm a");

      const dateObjEnd = parse(item?.startTime?.end, "HH:mm", new Date());
      const end = format(dateObjEnd, "hh:mm a");

      const durationInHour = getTimeDifference(start, end);

      return {
        startDate: item?.startTime?.date,
        startTime: {
          start,
          end,
        },
        durationInHour,
      };
    });

    const payload = {
      spaceTypeInt: 2,
      branchId,
      selectedDateAndTimes,
      numberOfSeats,
    };
    dispatch(getMeetingRoomsList(payload));
  };

  const fetchWorkspaceList = (branchId, values) => {
    const { dateAndTimes, workShopDetails } = values || {};
    const { numberOfSeats } = workShopDetails || {};

    if (!dateAndTimes?.selectedDates?.length) {
      dispatch(
        showAlert({
          text: t("Please select date and time slots for available Workspace"),
          type: "error",
        })
      );
      return;
    }

    if (!numberOfSeats) {
      dispatch(
        showAlert({
          text: t("Please enter number of seats for available Workspace"),
          type: "error",
        })
      );

      return;
    }

    const selectedDateAndTimes = dateAndTimes?.selectedDates?.map((item) => {
      const dateObjStart = parse(item?.startTime?.start, "HH:mm", new Date());
      const start = format(dateObjStart, "hh:mm a");

      const dateObjEnd = parse(item?.startTime?.end, "HH:mm", new Date());
      const end = format(dateObjEnd, "hh:mm a");

      const durationInHour = getTimeDifference(start, end);

      return {
        startDate: item?.startTime?.date,
        startTime: {
          start,
          end,
        },
        durationInHour,
      };
    });

    const payload = {
      spaceTypeInt: 1,
      branchId,
      selectedDateAndTimes,
      numberOfSeats,
    };
    dispatch(getWorkspaceList(payload));
  };

  const fetchWorkspaceBookingDetails = (spaceId, values) => {
    const { dateAndTimes, workShopDetails } = values || {};
    const { numberOfSeats } = workShopDetails || {};

    if (!dateAndTimes?.selectedDates?.length) {
      dispatch(
        showAlert({
          text: t("Please select date and time slots for Workspace Details"),
          type: "error",
        })
      );
      return;
    }

    if (!numberOfSeats) {
      dispatch(
        showAlert({
          text: t("Please enter number of seats for Workspace Details"),
          type: "error",
        })
      );

      return;
    }

    const selectedDateAndTimes = dateAndTimes?.selectedDates?.map((item) => {
      const dateObjStart = parse(item?.startTime?.start, "HH:mm", new Date());
      const start = format(dateObjStart, "hh:mm a");

      const dateObjEnd = parse(item?.startTime?.end, "HH:mm", new Date());
      const end = format(dateObjEnd, "hh:mm a");

      const durationInHour = getTimeDifference(start, end);

      return {
        startDate: item?.startTime?.date,
        startTime: {
          start,
          end,
        },
        durationInHour,
      };
    });

    const payload = {
      id: spaceId,
      date: selectedDateAndTimes[0]?.startDate,
    };
    dispatch(getWorkspaceBookingDetails(payload));
  };

  const fetchBranchAvailability = (branchId, values, setFieldValue) => {
    const { dateAndTimes, workShopDetails } = values || {};
    const { numberOfSeats } = workShopDetails || {};

    if (!dateAndTimes?.selectedDates?.length) {
      dispatch(
        showAlert({
          text: t("Please select date and time slots for available Branch"),
          type: "error",
        })
      );
      return;
    }

    if (!numberOfSeats) {
      dispatch(
        showAlert({
          text: t("Please enter number of seats for available Branch"),
          type: "error",
        })
      );

      return;
    }

    const selectedDateAndTimes = dateAndTimes?.selectedDates?.map((item) => {
      const dateObjStart = parse(item?.startTime?.start, "HH:mm", new Date());
      const start = format(dateObjStart, "hh:mm a");

      const dateObjEnd = parse(item?.startTime?.end, "HH:mm", new Date());
      const end = format(dateObjEnd, "hh:mm a");

      const durationInHour = getTimeDifference(start, end);

      return {
        startDate: item?.startTime?.date,
        startTime: {
          start,
          end,
        },
        durationInHour,
      };
    });

    const payload = {
      id: branchId,
      date: selectedDateAndTimes[0]?.startDate,
    };
    dispatch(checkEntireBranch(payload))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          console.log(data);
          if (data?.length > 0) {
          } else {
            dispatch(
              showAlert({
                text: "Branch can not be selected,It already has some Bookings",
                type: "error",
              })
            );
            setFieldValue("branchId", "");
          }
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    if (
      (mode === "edit" || mode === "editWorkshopRequest") &&
      workshopDetails
    ) {
      // Destructure keys from meetingDetails
      const {
        name,
        nameAr,
        description,
        descriptionAr,
        topics,
        topicsAr,
        imageUrls,
        cityId,
        branchId,
        childSpaceId,
        amenities,
        status,
        dateAndTimes,
        workShopDetails,
        shareScheduleDate,
        dateAndTimesDetails,
        selectedDateAndTimes,
        customerInfo,
        workshopType,
      } = workshopDetails || {};

      const updatedSelectedDateAndTimes =
        selectedDateAndTimes?.map((item) => {
          const parsedStart = item?.startTime?.start
            ? parse(item?.startTime?.start, "hh:mm a", new Date())
            : null;
          const parsedEnd = item?.startTime?.end
            ? parse(item?.startTime?.end, "hh:mm a", new Date())
            : null;

          return {
            startTime: {
              date: item?.startDate,
              start:
                parsedStart && isValid(parsedStart)
                  ? format(parsedStart, "HH:mm")
                  : null,
              end:
                parsedEnd && isValid(parsedEnd)
                  ? format(parsedEnd, "HH:mm")
                  : null,
            },
          };
        }) || [];

      const updatedDateAndTimes = {
        moreDays: dateAndTimesDetails?.moreDays,
        numberOfDays: dateAndTimesDetails?.numberOfDays,
        selectedDates: updatedSelectedDateAndTimes,
      };

      cityId?._id && fetchBranchesByCity(cityId?._id);

      // CALL MEETING ROOMS API
      const updatedSelectedDateAndTimesForMeetingRoom =
        selectedDateAndTimes?.map((item) => ({
          startDate: item?.startDate,
          startTime: {
            start: item?.startTime?.start,
            end: item?.startTime?.end,
          },
          durationInHour: item?.durationInHour,
        })) || [];

      const updatedPayload = {
        spaceTypeInt: 2,
        branchId: branchId?._id,
        ...(mode !== "edit" && {
          selectedDateAndTimes: updatedSelectedDateAndTimesForMeetingRoom,
          numberOfSeats: workShopDetails?.numberOfSeats,
        }),
      };

      branchId?._id && dispatch(getMeetingRoomsList(updatedPayload));

      const updatedAmenities = amenities?.map((item) => item?._id) || [];
      // Update formValues using destructured values
      setFormValues((prevValues) => ({
        ...prevValues,
        name,
        nameAr,
        description,
        descriptionAr,
        topics,
        topicsAr,
        imageUrls,
        cityId: cityId?._id,
        branchId: branchId?._id,
        childSpaceId,
        amenities: updatedAmenities,
        status,
        dateAndTimes: updatedDateAndTimes,
        workShopDetails,
        shareScheduleDate: shareScheduleDate || "",
        bankInfo: workShopDetails?.bankInfo,
        customerInfo: customerInfo,
        workshopType: workshopType,
      }));
    }
  }, [mode, workshopDetails]);

  useEffect(() => {
    if (mode === "editWorkshopRequest" && workshopDetails) {
      const { childSpaceId } = workshopDetails || {};
      const updatedChildSpaceId =
        meetingRoomsList?.find((option) => option._id === childSpaceId)?._id ||
        "";
      setFormValues((prevValues) => ({
        ...prevValues,
        childSpaceId: updatedChildSpaceId,
      }));
    }
  }, [workshopDetails]);

  const handleOnSubmit = (values, { setSubmitting }) => {
    const buttonId = document.activeElement.id;
    // UPDATE STATUS ACCORDING TO ACTION TYPE
    const updatedStatus =
      buttonId === "draftBtn" ? 3 : buttonId === "submitBtn" ? 1 : 1;

    const {
      spaceTypeInt,
      spaceType,
      name,
      nameAr,
      description,
      descriptionAr,
      topics,
      topicsAr,
      imageUrls,
      cityId,
      branchId,
      childSpaceId,
      amenities,
      status,
      dateAndTimes,
      workShopDetails,
      shareScheduleDate,
      bankInfo,
      customerInfo,
      workshopType,
    } = values;

    const { trainerShare, companyShare, tax } = workShopDetails || {};
    const total =
      toNumber(trainerShare) + toNumber(companyShare) + toNumber(tax);
    if (total !== 100) {
      dispatch(
        showAlert({ text: "Total share must be exact 100%", type: "error" })
      );
      return;
    }

    let data = new FormData();
    data.append("name", name);
    data.append("nameAr", nameAr);
    data.append("description", description);
    data.append("descriptionAr", descriptionAr);
    data.append("topics", topics);
    data.append("topicsAr", topicsAr);
    const filesToUpload = imageUrls?.map((fileObj) => fileObj);
    filesToUpload.forEach((file) => {
      data.append("imageUrls", file);
    });

    data.append("cityId", cityId);
    data.append("branchId", branchId);

    if (workshopType !== 3) {
      data.append("childSpaceId", childSpaceId);
    }
    data.append("workshopType", workshopType);

    selectedPeriods.forEach((period, index) => {
      data.append(`timePeriods[${index}][name]`, period.name);
      data.append(`timePeriods[${index}][nameAr]`, "الربيع"); // or your translation logic
      data.append(`timePeriods[${index}][numberOfSeats]`, period.numberOfSeats);
      data.append(`timePeriods[${index}][price]`, period.price);
      data.append(`timePeriods[${index}][discount]`, period.discount);
      data.append(`timePeriods[${index}][time][start]`, period.time.start);
      data.append(`timePeriods[${index}][time][end]`, period.time.end);
    });

    amenities.forEach((amenity, index) => {
      data.append(`amenities[${index}]`, amenity);
    });

    data.append("workShopDetails[trainerShare]", workShopDetails?.trainerShare);
    data.append("workShopDetails[companyShare]", workShopDetails?.companyShare);
    data.append("workShopDetails[tax]", workShopDetails?.tax || 0);
    data.append("workShopDetails[trainerName]", workShopDetails?.trainerName);
    (mode === "add" || mode === "edit") &&
      data.append(
        "workShopDetails[socialMediaAccount]",
        customerInfo?.socialMediaAccount
      );
    data.append(
      "workShopDetails[ageGroup][min]",
      workShopDetails?.ageGroup.min
    );
    data.append(
      "workShopDetails[ageGroup][max]",
      workShopDetails?.ageGroup.max
    );
    data.append(
      "workShopDetails[numberOfSeats]",
      workShopDetails?.numberOfSeats
    );
    data.append("workShopDetails[price]", workShopDetails?.price);
    data.append("workShopDetails[discount]", 0);
    data.append("workShopDetails[gender]", workShopDetails?.gender);

    // console.log(dateAndTimes.selectedDates);

    data.append("dateAndTimesDetails[moreDays]", dateAndTimes?.moreDays);
    data.append(
      "dateAndTimesDetails[numberOfDays]",
      dateAndTimes?.numberOfDays
    );

    dateAndTimes?.selectedDates?.forEach((item, index) => {
      const dateObjStart = parse(item?.startTime?.start, "HH:mm", new Date());
      const formattedTime = format(dateObjStart, "hh:mm a");
      const dateObjEnd = parse(item?.startTime?.end, "HH:mm", new Date());
      const formattedEnd = format(dateObjEnd, "hh:mm a");
      const timeDifference = getTimeDifference(formattedTime, formattedEnd);
      data.append(
        `selectedDateAndTimes[${index}][startDate]`,
        item?.startTime?.date
      );
      data.append(
        `selectedDateAndTimes[${index}][startTime][start]`,
        formattedTime
      );
      data.append(
        `selectedDateAndTimes[${index}][startTime][end]`,
        formattedEnd
      );
      data.append(
        `selectedDateAndTimes[${index}][durationInHour]`,
        timeDifference
      );
    });
    shareScheduleDate && data.append("shareScheduleDate", shareScheduleDate);

    // BANK DETAILS
    data.append(
      "workShopDetails[bankInfo][accountHolder]",
      bankInfo?.accountHolder
    );
    data.append("workShopDetails[bankInfo][bankName]", bankInfo?.bankName);
    data.append("workShopDetails[bankInfo][IBAN]", bankInfo?.IBAN);

    if (mode === "add") {
      data.append("spaceTypeInt", "3"); // STATIC FOR WORKSHOP
      data.append("spaceType", "workShop"); // STATIC FOR WORKSHOP
      data.append("status", updatedStatus);

      dispatch(addWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      data.append("spaceTypeInt", "3"); // STATIC FOR WORKSHOP
      data.append("spaceType", "workShop"); // STATIC FOR WORKSHOP
      data.append("status", updatedStatus);

      data.append("id", id);
      dispatch(updateWorkspace(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "editWorkshopRequest") {
      data.append("id", id);
      data.append("customerInfo[name]", customerInfo?.name);
      data.append("customerInfo[phoneCode]", customerInfo?.phoneCode);
      data.append("customerInfo[phoneNumber]", customerInfo?.phoneNumber);
      data.append(
        "customerInfo[socialMediaAccount]",
        customerInfo?.socialMediaAccount
      );

      dispatch(updateWorkshopRequest(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  // Handle date and time selection
  const handleDateTimeSelection = (values, setFieldValue, datetime12h) => {
    if (!datetime12h)
      return dispatch(
        showAlert({ text: t("Please Select Date"), type: "error" })
      );

    const formattedStartTime = convertTo24HourFormat(startTime);
    const formattedEndTime = convertTo24HourFormat(endTime);
    const formattedDate = format(datetime12h, "yyyy-MM-dd'T'00:00:00.000'Z'");

    const currentSelectedDates = values?.dateAndTimes?.selectedDates || [];

    // Handle single or multiple date selection
    if (!values?.dateAndTimes?.moreDays) {
      // If `moreDays` is false, allow only one date
      if (currentSelectedDates.length > 0) {
        return dispatch(
          showAlert({
            text: t("You can only select one date"),
            type: "error",
          })
        );
      }
    } else {
      // If `moreDays` is true, enforce `numberOfDays` limit
      if (currentSelectedDates.length >= values?.dateAndTimes?.numberOfDays) {
        return dispatch(
          showAlert({
            text: `You can only select ${values?.dateAndTimes?.numberOfDays} dates`,
            type: "error",
          })
        );
      }
    }

    // Check if selected time is in the past for today
    if (isPastTime(formattedDate, formattedStartTime, formattedEndTime)) {
      return dispatch(
        showAlert({
          text: t("Selected time cannot be in the past"),
          type: "error",
        })
      );
    }

    const updatedDateAndTime = {
      startTime: {
        date: formattedDate,
        start: formattedStartTime,
        end: formattedEndTime,
      },
    };

    // Check for duplicate exact time slot
    if (
      isExactDuplicate(
        currentSelectedDates,
        formattedDate,
        formattedStartTime,
        formattedEndTime
      )
    ) {
      return dispatch(
        showAlert({
          text: t("This time slot is already selected"),
          type: "error",
        })
      );
    }

    // Check if end time is earlier than start time
    if (formattedEndTime <= formattedStartTime) {
      return dispatch(
        showAlert({
          text: t("End time must be later than start time"),
          type: "error",
        })
      );
    }

    // Check for overlapping time slots on the same date
    if (
      isOverlapping(
        currentSelectedDates,
        formattedDate,
        formattedStartTime,
        formattedEndTime
      )
    ) {
      return dispatch(
        showAlert({
          text: t("This time range overlaps with an already selected slot"),
          type: "error",
        })
      );
    }

    setFieldValue("dateAndTimes.selectedDates", [
      ...currentSelectedDates,
      updatedDateAndTime,
    ]);

    const newSelectedDates = [...currentSelectedDates, updatedDateAndTime];

    setFormValues({
      name: values.name,
      nameAr: values.nameAr,
      description: values.description,
      descriptionAr: values.descriptionAr,
      topics: values.topics,
      topicsAr: values.topicsAr,
      imageUrls: values.imageUrls,
      cityId: values.cityId,
      amenities: values.amenities,
      status: values.status,
      workShopDetails: values.workShopDetails,
      shareScheduleDate: values.shareScheduleDate,
      bankInfo: values.bankInfo,
      customerInfo: values.customerInfo,
      dateAndTimes: {
        ...values.dateAndTimes,
        selectedDates: [...currentSelectedDates, updatedDateAndTime],
      },
      branchId: "", // Clear this field
      childSpaceId: "", // Clear this field
    });

    setFieldValue("branchId", "");
    setFieldValue("childSpaceId", "");
  };

  const isExactDuplicate = (
    selectedDates,
    formattedDate,
    formattedStartTime,
    formattedEndTime
  ) => {
    return selectedDates.some(
      (item) =>
        item.startTime.date === formattedDate &&
        item.startTime.start === formattedStartTime &&
        item.startTime.end === formattedEndTime
    );
  };

  const isOverlapping = (
    selectedDates,
    formattedDate,
    formattedStartTime,
    formattedEndTime
  ) => {
    return selectedDates.some((item) => {
      if (item.startTime.date === formattedDate) {
        const existingStart = item.startTime.start;
        const existingEnd = item.startTime.end;

        return (
          (formattedStartTime >= existingStart &&
            formattedStartTime < existingEnd) || // New start is within an existing range
          (formattedEndTime > existingStart &&
            formattedEndTime <= existingEnd) || // New end is within an existing range
          (formattedStartTime <= existingStart &&
            formattedEndTime >= existingEnd) // New range fully covers an existing one
        );
      }
      return false;
    });
  };

  const isPastTime = (formattedDate, formattedStartTime, formattedEndTime) => {
    const today = format(new Date(), "yyyy-MM-dd'T'00:00:00.000'Z'");

    if (formattedDate === today) {
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTime = `${String(currentHours).padStart(2, "0")}:${String(
        currentMinutes
      ).padStart(2, "0")}`;

      return formattedStartTime < currentTime || formattedEndTime < currentTime;
    }
    return false;
  };

  // Remove a selected date
  const handleRemoveDate = (values, setFieldValue, indexToRemove) => {
    const updatedDates = values?.dateAndTimes?.selectedDates?.filter(
      (_, index) => index !== indexToRemove
    );
    setFieldValue("dateAndTimes.selectedDates", updatedDates);
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workshop_page">
        {loading && (mode === "edit" || mode === "editWorkshopRequest") ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={
                  mode === "add"
                    ? t("Add New Workshop")
                    : mode === "edit"
                    ? t("Edit Workshop")
                    : t("Edit Workshop Request")
                }
              />
              <Formik
                initialValues={formValues}
                validationSchema={createWorkshopSchema}
                enableReinitialize={true}
                onSubmit={handleOnSubmit}
              >
                {({ values, setFieldValue, errors, touched }) => (
                  <Form>
                    <Row className="common_wrapper">
                      <Col lg={12}>
                        <FileUpload
                          uploadedFiles={values.imageUrls || []}
                          setUploadedFiles={(files) =>
                            setFieldValue("imageUrls", files)
                          }
                          maxFiles={5}
                          maxFileSize={0.6 * 1024 * 1024}
                          acceptedFileTypes={[
                            "image/png",
                            "image/jpeg",
                            "image/svg+xml",
                          ]}
                          requireSquare={false}
                          requireRectangle={false}
                          // onChange={handleFilesChange}
                          className={
                            touched?.imageUrls && errors?.imageUrls
                              ? "invalid"
                              : ""
                          }
                        />
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="name"
                            label={t("English Title")}
                          />
                          <CustomField
                            name="name"
                            type="text"
                            placeholder={t("Enter English Title")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="nameAr"
                            label={t("Arabic Title")}
                          />
                          <CustomField
                            name="nameAr"
                            type="text"
                            placeholder={t("Enter Arabic Title")}
                            touched={touched}
                            errors={errors}
                            isArabic={true}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="description"
                            label={t("English Description")}
                          />
                          <CustomField
                            name="description"
                            placeholder={t("Enter English Description")}
                            touched={touched}
                            errors={errors}
                            as="textarea"
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="descriptionAr"
                            label={t("Arabic Description")}
                          />
                          <CustomField
                            name="descriptionAr"
                            placeholder={t("Enter Arabic Description")}
                            touched={touched}
                            errors={errors}
                            as="textarea"
                            isArabic={true}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="topics"
                            label={t("English Workshop Topic")}
                          />
                          <CustomField
                            name="topics"
                            placeholder={t("Enter Topic")}
                            touched={touched}
                            errors={errors}
                            as="textarea"
                          />
                          {/* <Editor
                            value={values?.topics}
                            className="custom_editor"
                            onTextChange={(e) => {
                              setFieldValue("topics", e.htmlValue);
                            }}
                          /> */}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="topicsAr"
                            label={t("Arabic Workshop Topic")}
                          />
                          <CustomField
                            name="topicsAr"
                            placeholder={t("Enter Topic")}
                            touched={touched}
                            errors={errors}
                            as="textarea"
                            isArabic={true}
                          />
                        </div>
                      </Col>

                      {/* DATE & TIME ROW */}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">{t("Date and Time")}</h2>
                          <Row>
                            <Col lg={6}>
                              <div style={{ maxWidth: 500 }}>
                                <Calendar
                                  id="calendar-12h"
                                  className="w-100"
                                  value={datetime12h}
                                  onChange={(e) => setDateTime12h(e.value)}
                                  // showTime
                                  hourFormat="12"
                                  inline
                                  minDate={new Date()}
                                />
                              </div>
                              <div className="d-flex align-items-start gap-4 ">
                                <TimePicker
                                  startTime={startTime}
                                  setStartTime={setStartTime}
                                  endTime={endTime}
                                  setEndTime={setEndTime}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDateTimeSelection(
                                      values,
                                      setFieldValue,
                                      datetime12h
                                    )
                                  }
                                  className="main_btn mt-3"
                                  disabled={
                                    values?.dateAndTimes?.numberOfDays == 0 ||
                                    (mode === "edit" &&
                                      values?.workshopType == 1)
                                  }
                                >
                                  {t("Add Date")}
                                </button>
                              </div>
                            </Col>
                            <Col lg={6}>
                              <Row>
                                {values?.dateAndTimes?.moreDays && (
                                  <Col lg={6}>
                                    <div className="form_field">
                                      <CustomLabel
                                        htmlFor={`dateAndTimes.numberOfDays`}
                                        label={t("Number of Days")}
                                      />
                                      <CustomField
                                        name={`dateAndTimes.numberOfDays`}
                                        type="number"
                                        placeholder={t("Enter Number of Days")}
                                        touched={touched}
                                        errors={errors}
                                        isReadOnly={
                                          mode === "edit" &&
                                          values?.workshopType == 1
                                        }
                                      />
                                    </div>
                                  </Col>
                                )}
                                <Col lg={12}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Switch
                                      checked={values?.dateAndTimes?.moreDays}
                                      handleOnChange={(e) => {
                                        const newMoreDaysValue =
                                          !values?.dateAndTimes?.moreDays;
                                        setFieldValue(
                                          "dateAndTimes.moreDays",
                                          newMoreDaysValue
                                        );

                                        if (!newMoreDaysValue) {
                                          setFieldValue(
                                            "dateAndTimes.numberOfDays",
                                            1
                                          );
                                          setFieldValue(
                                            "dateAndTimes.selectedDates",
                                            []
                                          );
                                        }
                                      }}
                                      disabled={
                                        mode === "edit" &&
                                        values?.workshopType == 1
                                      }
                                    />
                                    <p className="fs_14 text_gray">
                                      {t("More Days")}
                                    </p>
                                  </div>
                                </Col>

                                <Col lg={12} className="mt-4">
                                  {values?.dateAndTimes?.selectedDates.map(
                                    (dateEntry, index) => (
                                      <div
                                        key={index}
                                        className="selected_date_time g_between"
                                      >
                                        <div className="d-flex gap-2">
                                          <CalendarPrimaryIcon
                                            width={18}
                                            height={18}
                                            color={primaryColor}
                                          />
                                          <span>
                                            {formatDate(
                                              dateEntry.startTime.date
                                            )}
                                          </span>
                                          <span>{`${formatTime(
                                            dateEntry.startTime.start
                                          )} - ${formatTime(
                                            dateEntry.startTime.end
                                          )}`}</span>
                                        </div>
                                        <button
                                          type="button"
                                          className="transparent_btn"
                                          onClick={() =>
                                            handleRemoveDate(
                                              values,
                                              setFieldValue,
                                              index
                                            )
                                          }
                                          disabled={
                                            mode === "edit" &&
                                            values?.workshopType == 1
                                          }
                                        >
                                          <CircleX className="text_danger" />
                                        </button>
                                      </div>
                                    )
                                  )}
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {/* WORKSHOP DETAILS */}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">
                            {t("Workshop Details")}
                          </h2>
                          <Row>
                            <Col lg={2}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.trainerShare"
                                  label={t("Trainer's share")}
                                />
                                <CustomField
                                  name="workShopDetails.trainerShare"
                                  type="number"
                                  placeholder={t("55%")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                            <Col lg={2}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.companyShare"
                                  label={t("Ashjar share")}
                                />
                                <CustomField
                                  name="workShopDetails.companyShare"
                                  type="number"
                                  placeholder={t("30%")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                            <Col lg={2}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.tax"
                                  label={t("Tax")}
                                />
                                <CustomField
                                  name="workShopDetails.tax"
                                  type="number"
                                  placeholder={t("15%")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.trainerName"
                                  label={t("Trainer Name")}
                                />
                                <CustomField
                                  name="workShopDetails.trainerName"
                                  type="text"
                                  placeholder={t("Enter Trainer Name")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                            {/* {mode === "editWorkshopRequest" && ( */}
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="customerInfo.socialMediaAccount"
                                  label={t("Trainer Name Link")}
                                />
                                <CustomField
                                  name="customerInfo.socialMediaAccount"
                                  type="text"
                                  placeholder={t("Trainer Name Link")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                            {/* )} */}
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.ageGroup"
                                  label={t("Age Group")}
                                />
                                <div className="d-flex gap-3">
                                  <CustomField
                                    name="workShopDetails.ageGroup.min"
                                    type="number"
                                    placeholder={t("Min")}
                                    touched={touched}
                                    errors={errors}
                                  />
                                  <CustomField
                                    name="workShopDetails.ageGroup.max"
                                    type="number"
                                    placeholder={t("Max")}
                                    touched={touched}
                                    errors={errors}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.gender"
                                  label={t("Gender")}
                                />
                                <SingleSelect
                                  options={genderOptions}
                                  value={values?.workShopDetails?.gender}
                                  placeholder={t("Select gender")}
                                  handleSingleSelectChange={(e) =>
                                    setFieldValue(
                                      "workShopDetails.gender",
                                      e.value
                                    )
                                  }
                                  className={`${
                                    errors?.workShopDetails?.gender &&
                                    touched?.workShopDetails?.gender
                                      ? "invalid"
                                      : ""
                                  }`}
                                  error={errors?.workShopDetails?.gender}
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <label htmlFor="workShopDetails.numberOfSeats">
                                  {t("Number of seat")}
                                </label>
                                <Field
                                  name="workShopDetails.numberOfSeats"
                                  type="number"
                                  placeholder={t("25")}
                                  className="form-control"
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setFieldValue(
                                      "workShopDetails.numberOfSeats",
                                      value
                                    );
                                    setFieldValue("branchId", "");
                                    setFieldValue("childSpaceId", "");
                                  }}
                                  readOnly={
                                    mode === "edit" && values?.workshopType == 1
                                  }
                                />
                                <ErrorMessage
                                  name="workShopDetails.numberOfSeats"
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.price"
                                  label={t("Price")}
                                />
                                <CustomField
                                  name="workShopDetails.price"
                                  type="number"
                                  placeholder={t("25")}
                                  touched={touched}
                                  errors={errors}
                                  isReadOnly={
                                    mode === "edit" && values?.workshopType == 1
                                  }
                                />
                              </div>
                            </Col>
                            {/* <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.discount"
                                  label={t("Discount Price")}
                                />
                                <CustomField
                                  name="workShopDetails.discount"
                                  type="number"
                                  placeholder={t("15")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col> */}
                          </Row>
                        </div>
                      </Col>

                      {/* LOCATION & CITY ROW */}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">{t("Location")}</h2>
                          <Row>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workshopType"
                                  label={t("Location Type")}
                                />
                                <SingleSelect
                                  options={locationTypeOptions}
                                  value={values?.workshopType}
                                  placeholder={t("Select Location Type")}
                                  handleSingleSelectChange={(e) => {
                                    dispatch(resetRejectedRooms());
                                    setFieldValue("workshopType", e.value);
                                    setFieldValue("branchId", null);
                                  }}
                                  className={`${
                                    errors?.workshopType &&
                                    touched?.workshopType
                                      ? "invalid"
                                      : ""
                                  }`}
                                  error={errors?.workshopType}
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="cityId"
                                  label={t("City")}
                                />
                                <SingleSelect
                                  options={citiesList?.map((item) => ({
                                    name: item?.name,
                                    value: item?._id,
                                  }))}
                                  value={values.cityId}
                                  placeholder={t("Select City")}
                                  handleSingleSelectChange={(e) => {
                                    dispatch(resetRejectedRooms());
                                    fetchBranchesByCity(e.value);
                                    setFieldValue("cityId", e.value);
                                    setFieldValue("branchId", null);
                                  }}
                                  className={`${
                                    errors.cityId && touched.cityId
                                      ? "invalid"
                                      : ""
                                  }`}
                                  error={errors?.cityId}
                                  disabled={
                                    mode === "edit" && values?.workshopType == 1
                                  }
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="branchId"
                                  label={t("Branch")}
                                />
                                <SingleSelect
                                  options={branchesByCity?.map((item) => ({
                                    name: item?.name,
                                    value: item?._id,
                                  }))}
                                  value={values.branchId}
                                  placeholder={t("Select Branch")}
                                  handleSingleSelectChange={(e) => {
                                    if (values.workshopType == 1) {
                                      fetchMeetingRoomsList(e.value, values);
                                    } else if (values.workshopType == 2) {
                                      fetchWorkspaceList(e.value, values);
                                    } else if (values.workshopType == 3) {
                                      dispatch(resetRejectedRooms());
                                      fetchBranchAvailability(
                                        e.value,
                                        values,
                                        setFieldValue
                                      );
                                    }
                                    setFieldValue("branchId", e.value);
                                    setFieldValue("childSpaceId", null);
                                  }}
                                  className={`${
                                    errors.branchId && touched.branchId
                                      ? "invalid"
                                      : ""
                                  }`}
                                  error={errors?.branchId}
                                  disabled={
                                    mode === "edit" && values?.workshopType == 1
                                  }
                                />
                              </div>
                            </Col>
                            {(values.workshopType == 1 ||
                              values.workshopType == 2) && (
                              <Col lg={6}>
                                <div className="form_field">
                                  {values.workshopType == 1 ? (
                                    <>
                                      <CustomLabel
                                        htmlFor="childSpaceId"
                                        label={t("Meeting Rooms")}
                                      />
                                      <SingleSelect
                                        options={meetingRoomsList?.map(
                                          (item) => ({
                                            name: item?.name,
                                            value: item?._id,
                                          })
                                        )}
                                        value={values.childSpaceId}
                                        placeholder={t("Select Meeting Room")}
                                        handleSingleSelectChange={(e) => {
                                          setFieldValue(
                                            "childSpaceId",
                                            e.value
                                          );
                                        }}
                                        className={`${
                                          errors.childSpaceId &&
                                          touched.childSpaceId
                                            ? "invalid"
                                            : ""
                                        }`}
                                        error={errors?.childSpaceId}
                                        disabled={
                                          mode === "edit" &&
                                          values?.workshopType == 1
                                        }
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <CustomLabel
                                        htmlFor="childSpaceId"
                                        label={t("Work Spaces")}
                                      />
                                      <SingleSelect
                                        options={workspaceList?.map((item) => ({
                                          name: item?.name,
                                          value: item?._id,
                                        }))}
                                        value={values.childSpaceId}
                                        placeholder={t("Select Work Space")}
                                        handleSingleSelectChange={(e) => {
                                          fetchWorkspaceBookingDetails(
                                            e.value,
                                            values
                                          );
                                          setFieldValue(
                                            "childSpaceId",
                                            e.value
                                          );
                                        }}
                                        className={`${
                                          errors.childSpaceId &&
                                          touched.childSpaceId
                                            ? "invalid"
                                            : ""
                                        }`}
                                        error={errors?.childSpaceId}
                                        disabled={
                                          mode === "edit" &&
                                          values?.workshopType == 1
                                        }
                                      />
                                    </>
                                  )}
                                </div>
                              </Col>
                            )}

                            <Col lg={6}>
                              {values.workshopType == 2 && (
                                <TimePeriodSelector
                                  workspaceBookingDetails={
                                    workspaceBookingDetails
                                  }
                                  selectedPeriods={selectedPeriods}
                                  setSelectedPeriods={setSelectedPeriods}
                                />
                              )}
                            </Col>
                          </Row>

                          {/* SHOW UNAVAILABLE MEETING ROOMS */}
                          {rejectedRooms?.length > 0 ? (
                            <>
                              <hr />
                              <h2 className="fs_20 text_danger">
                                {t("Unavailable Meeting rooms")}
                              </h2>

                              <ul>
                                {rejectedRooms?.map((item, index) => (
                                  <li
                                    className="text_danger"
                                    style={{ listStyle: "disc" }}
                                  >
                                    {item?.reason === "insufficient_seats" &&
                                      `Seats not available in ${item?.room?.name} meeting room, Only ${item?.room?.totalSeats} seats are available `}
                                    {item?.reason === "unavailable_slots" &&
                                      `Slots not available in ${
                                        item?.room?.name
                                      } meeting room, On ${formatDate(
                                        item?.date
                                      )}`}
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : null}
                        </div>
                      </Col>

                      {/* WORKSHOP  */}
                      {/* {mode === "editWorkshopRequest" && ( */}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">
                            {t("Bank Information")}
                          </h2>
                          <Row>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="bankInfo.accountHolder"
                                  label={t("Account Holder Name")}
                                />
                                <CustomField
                                  name="bankInfo.accountHolder"
                                  type="text"
                                  placeholder={t("Enter Account Holder Name")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="bankInfo.bankName"
                                  label={t("Bank Name")}
                                />
                                <SingleSelect
                                  options={banksList?.map((item) => ({
                                    name: item?.name,
                                    value: item?.id,
                                  }))}
                                  value={values?.bankInfo?.bankName}
                                  placeholder={t("Select Bank")}
                                  handleSingleSelectChange={(e) => {
                                    setFieldValue("bankInfo.bankName", e.value);
                                  }}
                                />
                              </div>
                            </Col>
                            <Col lg={4}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="bankInfo.IBAN"
                                  label={t("IBAN")}
                                />
                                <CustomField
                                  name="bankInfo.IBAN"
                                  type="text"
                                  placeholder={t("Enter IBAN")}
                                  touched={touched}
                                  errors={errors}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                      {/* )} */}

                      {/* AMENITIES ROW*/}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">{t("Amenities")}</h2>
                          <Row>
                            <Col lg={6}>
                              <div className="mb-3 form_field">
                                <CustomLabel
                                  htmlFor={`amenities`}
                                  label={t("Select Amenities")}
                                />
                                <MultipleSelect
                                  options={amenitiesList?.map((item) => ({
                                    name: item?.name,
                                    value: item?._id,
                                  }))}
                                  value={values.amenities}
                                  placeholder={t("Select Amenities")}
                                  handleMultipleSelectChange={(e) => {
                                    setFieldValue(`amenities`, e);
                                  }}
                                  className={`${
                                    touched.amenities && errors.amenities
                                      ? "invalid"
                                      : ""
                                  }`}
                                />
                                <ErrorMessage
                                  name={`amenities`}
                                  component="div"
                                  className="text-danger"
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {/* SHARE SCHEDULE */}
                      <Col lg={12} style={{ marginTop: 21 }}>
                        <div className="bg_white br_14 p_23_30">
                          <h2 className="fs_20 mb-4">{t("Share Schedule")}</h2>
                          <Row>
                            <Col lg={6}>
                              <div className="form_field">
                                <CustomLabel
                                  htmlFor="workShopDetails.shareScheduleDate"
                                  label={t("Date")}
                                />
                                <DatePicker
                                  date={values.shareScheduleDate}
                                  onChange={(e) =>
                                    setFieldValue("shareScheduleDate", e.value)
                                  }
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>
                    <Row className="px-0 my-4">
                      <FormButtons
                        isDraftBtn={
                          mode === "editWorkshopRequest" ? false : true
                        }
                        isLoading={isLoading}
                        addBtnText={mode === "add" ? t("Add New") : t("Update")}
                      />
                    </Row>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddWorkshop;
